import React from 'react';
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { BarChart2, Bot, Grid2x2 as Grid, Wallet, TrendingUp, TrendingDown, DollarSign, BarChart } from 'lucide-react';
import { useBots } from '@/contexts/BotsContext';
import { Trade } from '../../utils/types';
import { formatValue, formatProfit, formatPercentage, formatSignedPercentage, formatCurrency } from '../../utils/formatValue';

export const StatsCards: React.FC = () => {
  const { bots, practiceBots, backtestResults } = useBots();
  
  // Add safety checks for data
  const safeBots = bots || [];
  const safePracticeBots = practiceBots || [];
  const safeBacktestResults = backtestResults || [];
  
  // Calculate total winning and losing trades
  let totalWinningTradesOverall = 0;
  let totalLosingTradesOverall = 0;
  let buyWinsOverall = 0;
  let buyLossesOverall = 0;
  let sellWinsOverall = 0;
  let sellLossesOverall = 0;
  let totalProfit = 0;
  let totalProfitAmountOverall = 0;
  let sumOfWinningProfitPercentages = 0;
  let sumOfLosingProfitPercentages = 0;
  
  // Calculate from bots data
  const allBots = [...safeBots, ...safePracticeBots];
  allBots.forEach(bot => {
    // Add profit (ensure it's a number)
    const botProfit = typeof bot.profit === 'number' ? bot.profit : 
      (bot.profit !== null && bot.profit !== undefined) ? parseFloat(String(bot.profit)) || 0 : 0;
    totalProfit += botProfit;
    totalProfitAmountOverall += botProfit;
    
    const botTotalTrades = typeof bot.total_trades === 'number' ? bot.total_trades : 
      typeof bot.totalTrades === 'number' ? bot.totalTrades : 
      (bot.total_trades !== null && bot.total_trades !== undefined) ? parseInt(String(bot.total_trades)) || 0 :
      (bot.totalTrades !== null && bot.totalTrades !== undefined) ? parseInt(String(bot.totalTrades)) || 0 : 0;
    
    const botWinRate = typeof bot.win_rate === 'number' ? bot.win_rate : 
      typeof bot.winRate === 'number' ? bot.winRate : 
      (bot.win_rate !== null && bot.win_rate !== undefined) ? parseFloat(String(bot.win_rate)) || 0 :
      (bot.winRate !== null && bot.winRate !== undefined) ? parseFloat(String(bot.winRate)) || 0 : 0;
    
    if (botTotalTrades && botWinRate) {
      const winningTrades = Math.floor(botTotalTrades * (botWinRate / 100));
      const losingTrades = botTotalTrades - winningTrades;
      
      totalWinningTradesOverall += winningTrades;
      totalLosingTradesOverall += losingTrades;
      
      // Estimate buy/sell distribution (assume 50/50 split)
      const buyTrades = Math.floor(botTotalTrades * 0.5);
      const sellTrades = botTotalTrades - buyTrades;
      
      // Estimate buy wins/losses
      const buyWinRateCalc = botWinRate; // Assume same win rate for buys
      buyWinsOverall += Math.floor(buyTrades * (buyWinRateCalc / 100));
      buyLossesOverall += buyTrades - Math.floor(buyTrades * (buyWinRateCalc / 100));
      
      // Estimate sell wins/losses
      const sellWinRateCalc = botWinRate; // Assume same win rate for sells
      sellWinsOverall += Math.floor(sellTrades * (sellWinRateCalc / 100));
      sellLossesOverall += sellTrades - Math.floor(sellTrades * (sellWinRateCalc / 100));
    }
  });
  
  // Calculate from backtest results if available
  let backtestProfit = 0;
  let backtestProfitAmount = 0;
  let backtestBuyWins = 0;
  let backtestBuyLosses = 0;
  let backtestBuyTotal = 0;
  let backtestSellWins = 0;
  let backtestSellLosses = 0;
  let backtestSellTotal = 0;
  let backtestWinningTrades = 0;
  let backtestLosingTrades = 0;
  let backtestTotalTrades = 0;
  
  (backtestResults || []).forEach(backtest => {
    // 🔥 FIX: Add NET profit (total_profit - fees) for accurate statistics
    const grossProfit = backtest.total_profit || 0;
    const fees = backtest.fees_total_usd || 0;
    const netProfit = grossProfit - fees;

    backtestProfit += netProfit;
    backtestProfitAmount += netProfit;
    
    // Calculate total trades from pairs data if available
    let totalTradesFromPairs = 0;
    if (backtest.pairs_data) {
      const pairsTotal = Object.values(backtest.pairs_data).reduce((total, pairData: any) => {
        return total + (pairData.trades || 0);
      }, 0);
      
      // Only use pairs data if it's greater than zero
      if (pairsTotal > 0) {
        totalTradesFromPairs = pairsTotal;
      }
    }
    
    // Use the larger value between pairs_data total and total_trades
    // Prefer pairs data as it's more accurate
    const actualTotalTrades = totalTradesFromPairs || (backtest.total_trades || 0);
    
    backtestTotalTrades += actualTotalTrades;
    
    // If we have trade history, use it for precise calculations
    if (backtest.trade_history && Array.isArray(backtest.trade_history)) {
      backtest.trade_history.forEach((trade: Trade) => {
        const isWin = (trade.profit_percentage || 0) > 0;
        const profitPercentage = trade.profit_percentage || 0;
        const isBuy = trade.type === 'buy';
        const isSell = trade.type === 'sell';
        
        // Accumulate profit percentages
        if (isWin) {
          sumOfWinningProfitPercentages += profitPercentage;
        } else {
          sumOfLosingProfitPercentages += Math.abs(profitPercentage);
        }
        
        // Count by trade type
        if (isBuy) backtestBuyTotal++;
        if (isSell) backtestSellTotal++;
        
        // Count wins and losses
        if (isWin) {
          backtestWinningTrades++;
          if (isBuy) backtestBuyWins++;
          if (isSell) backtestSellWins++;
        } else {
          backtestLosingTrades++;
          if (isBuy) backtestBuyLosses++;
          if (isSell) backtestSellLosses++;
        }
      });
    } else if (actualTotalTrades && (backtest.win_rate_pct || backtest.win_rate)) {
      // If no trade history, estimate from summary data
      // NOTE: win_rate_pct is PERCENTAGE (73.75), convert to fraction for calculation
      const winRatePct = backtest.win_rate_pct || backtest.win_rate || 0;
      const winRateFraction = winRatePct / 100;
      const winningTrades = Math.floor(actualTotalTrades * winRateFraction);
      const losingTrades = actualTotalTrades - winningTrades;

      backtestWinningTrades += winningTrades;
      backtestLosingTrades += losingTrades;

      // Estimate buy/sell distribution (assume 50/50 split)
      const buyTrades = Math.floor(actualTotalTrades * 0.5);
      const sellTrades = actualTotalTrades - buyTrades;

      backtestBuyTotal += buyTrades;
      backtestSellTotal += sellTrades;

      // Estimate buy wins/losses (use same win rate fraction)
      backtestBuyWins += Math.floor(buyTrades * winRateFraction);
      backtestBuyLosses += buyTrades - Math.floor(buyTrades * winRateFraction);

      // Estimate sell wins/losses (use same win rate fraction)
      backtestSellWins += Math.floor(sellTrades * winRateFraction);
      backtestSellLosses += sellTrades - Math.floor(sellTrades * winRateFraction);
    }
  });
  
  // If we have no data from bots, use backtest data
  if (totalWinningTradesOverall === 0 && totalLosingTradesOverall === 0) {
    totalWinningTradesOverall = backtestWinningTrades;
    totalLosingTradesOverall = backtestLosingTrades;
    buyWinsOverall = backtestBuyWins;
    buyLossesOverall = backtestBuyLosses;
    sellWinsOverall = backtestSellWins;
    sellLossesOverall = backtestSellLosses;
    totalProfit = backtestProfit;
    totalProfitAmountOverall = backtestProfitAmount;
  }
  
  // Calculate win rate
  const totalTrades = totalWinningTradesOverall + totalLosingTradesOverall;
  const overallWinRate = totalTrades > 0 ? 
    (totalWinningTradesOverall / totalTrades) * 100 : 0;
  
  const buyTotal = buyWinsOverall + buyLossesOverall;
  const sellTotal = sellWinsOverall + sellLossesOverall;
  
  const buyWinRate = buyTotal > 0 ? (buyWinsOverall / buyTotal) * 100 : 0;
  const sellWinRate = sellTotal > 0 ? (sellWinsOverall / sellTotal) * 100 : 0;
  
  // Calculate average win/loss percentages
  const averageWinPercentage = totalWinningTradesOverall > 0 ? sumOfWinningProfitPercentages / totalWinningTradesOverall : 0;
  const averageLossPercentage = totalLosingTradesOverall > 0 ? sumOfLosingProfitPercentages / totalLosingTradesOverall : 0;
  
  // Calculate profit factor
  const profitFactor = sumOfLosingProfitPercentages > 0 ? sumOfWinningProfitPercentages / sumOfLosingProfitPercentages : 0;
  
  // Format profit with sign (ensure it's a number)
  const safeProfit = (typeof totalProfit === 'number' && !isNaN(totalProfit)) ? totalProfit : 0;
  const formattedProfit = safeProfit >= 0 ? `+$${safeProfit.toFixed(2)}` : `-$${Math.abs(safeProfit).toFixed(2)}`;
  
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-md border border-gray-200 dark:border-gray-700"
      >
        <div className="flex justify-between">
          <div>
            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Profit</p>
            <p className={`text-2xl font-bold ${totalProfit >= 0 ? 'text-green-500' : 'text-red-500'}`}>
              {formatValue(formattedProfit)}
            </p>
          </div>
          <div className="h-12 w-12 bg-green-100 dark:bg-green-900/20 rounded-lg flex items-center justify-center">
            <BarChart2 className="h-6 w-6 text-green-500" />
          </div>
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
          Based on your trading activity
        </p>
      </motion.div>

      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.1 }}
        className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-md border border-gray-200 dark:border-gray-700"
      >
        <div className="flex justify-between">
          <div>
            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Active Bots</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">
              {formatValue(allBots.filter(bot => bot.isActive).length)}
            </p>
          </div>
          <div className="h-12 w-12 bg-blue-100 dark:bg-blue-900/20 rounded-lg flex items-center justify-center">
            <Bot className="h-6 w-6 text-blue-500" />
          </div>
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
          Currently active trading bots
        </p>
      </motion.div>

      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.2 }}
        className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-md border border-gray-200 dark:border-gray-700"
      >
        <div className="flex justify-between">
          <div>
            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Trades</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">
              {formatValue(totalTrades || backtestTotalTrades || 0)}
            </p>
          </div>
          <div className="h-12 w-12 bg-purple-100 dark:bg-purple-900/20 rounded-lg flex items-center justify-center">
            <Grid className="h-6 w-6 text-purple-500" />
          </div>
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
          Total executed trades
        </p>
      </motion.div>

      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.3 }}
        className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-md border border-gray-200 dark:border-gray-700"
      >
        <div className="flex justify-between">
          <div>
            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Win Rate</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">
              {formatValue((typeof overallWinRate === 'number' && !isNaN(overallWinRate)) ? overallWinRate.toFixed(1) : '0.0')}%
            </p>
          </div>
          <div className="h-12 w-12 bg-pink-100 dark:bg-pink-900/20 rounded-lg flex items-center justify-center">
            <Wallet className="h-6 w-6 text-pink-500" />
          </div>
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
          Trading success rate
        </p>
      </motion.div>
      
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.4 }}
        className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-md border border-gray-200 dark:border-gray-700 col-span-1 md:col-span-2 lg:col-span-4"
      >
        <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Trade Performance</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Winning vs Losing Trades</h4>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-green-500" />
                  <p className="text-sm text-gray-600 dark:text-gray-400">Winning Trades</p>
                </div>
                <p className="text-xl font-bold text-green-500 dark:text-green-400">{totalWinningTradesOverall}</p>
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <TrendingDown className="h-4 w-4 text-red-500" />
                  <p className="text-sm text-gray-600 dark:text-gray-400">Losing Trades</p>
                </div>
                <p className="text-xl font-bold text-red-500 dark:text-red-400">{totalLosingTradesOverall}</p>
              </div>
            </div>
            <div className="mt-3 relative pt-1">
              <div className="flex h-2 overflow-hidden text-xs bg-gray-200 dark:bg-gray-600 rounded-full">
                <div 
                  style={{ width: `${overallWinRate}%` }}
                  className="flex flex-col text-center whitespace-nowrap text-white justify-center bg-green-500"
                ></div>
              </div>
              <div className="flex justify-between mt-1">
                <span className="text-xs text-gray-500 dark:text-gray-400">Win Rate: {overallWinRate.toFixed(1)}%</span>
                <span className="text-xs text-gray-500 dark:text-gray-400">Total: {totalTrades || 0}</span>
              </div>
              <div className="flex justify-between mt-1">
                <span className="text-xs text-gray-500 dark:text-gray-400">Winning: {totalWinningTradesOverall || 0}</span>
                <span className="text-xs text-gray-500 dark:text-gray-400">Losing: {totalLosingTradesOverall || 0}</span>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Buy Orders</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Buy Wins</p>
                <p className="text-xl font-bold text-green-500 dark:text-green-400">{buyWinsOverall}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Buy Losses</p>
                <p className="text-xl font-bold text-red-500 dark:text-red-400">{buyLossesOverall}</p>
              </div>
            </div>
            <div className="mt-3 relative pt-1">
              <div className="flex h-2 overflow-hidden text-xs bg-gray-200 dark:bg-gray-600 rounded-full">
                <div 
                  style={{ width: `${buyWinRate}%` }}
                  className="flex flex-col text-center whitespace-nowrap text-white justify-center bg-blue-500"
                ></div>
              </div>
              <div className="flex justify-between mt-1">
                <span className="text-xs text-gray-500 dark:text-gray-400">Win Rate: {buyWinRate.toFixed(1)}%</span>
                <span className="text-xs text-gray-500 dark:text-gray-400">Total: {buyTotal || 0}</span>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Sell Orders</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Sell Wins</p>
                <p className="text-xl font-bold text-green-500 dark:text-green-400">{sellWinsOverall}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Sell Losses</p>
                <p className="text-xl font-bold text-red-500 dark:text-red-400">{sellLossesOverall}</p>
              </div>
            </div>
            <div className="mt-3 relative pt-1">
              <div className="flex h-2 overflow-hidden text-xs bg-gray-200 dark:bg-gray-600 rounded-full">
                <div 
                  style={{ width: `${sellWinRate}%` }}
                  className="flex flex-col text-center whitespace-nowrap text-white justify-center bg-purple-500"
                ></div>
              </div>
              <div className="flex justify-between mt-1">
                <span className="text-xs text-gray-500 dark:text-gray-400">Win Rate: {sellWinRate.toFixed(1)}%</span>
                <span className="text-xs text-gray-500 dark:text-gray-400">Total: {sellTotal || 0}</span>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Performance Metrics</h4>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">Average Win</span>
                <span className="text-sm font-medium text-green-600 dark:text-green-400">
                  {totalWinningTradesOverall > 0 ? formatSignedPercentage(averageWinPercentage) : 'N/A'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">Average Loss</span>
                <span className="text-sm font-medium text-red-600 dark:text-red-400">
                  {totalLosingTradesOverall > 0 ? formatSignedPercentage(-averageLossPercentage) : 'N/A'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">Profit Factor</span>
                <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
                  {profitFactor > 0 ? profitFactor.toFixed(2) : 'N/A'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};